import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import SocialShareButtons from '../components/SocialShareButtons';
import { 
  ArrowLeft, 
  ExternalLink,
  Check,
  Zap,
  Shield,
  Award,
  MessageCircle,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Affiliate LifeWave Shop Link
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';
const WHATSAPP_NUMBER = '40766475946';

// Product benefits based on category - with translations
const productBenefits = {
  en: {
    'Phototherapy Patches': [
      'No drugs, chemicals, or stimulants',
      'Patented phototherapy technology',
      'Easy to use - just apply and go',
      'Non-transdermal delivery system',
      'Clinically tested and proven'
    ],
    'Y-Age System': [
      'Anti-aging support',
      'Promotes overall wellness',
      'No drugs or chemicals',
      'Convenient patch application',
      'Part of complete wellness system'
    ],
    'Alavida Skincare': [
      'Reduces appearance of fine lines',
      'Brightens complexion',
      'Improves skin firmness',
      'Natural ingredients',
      'Professional-grade formula'
    ],
    'Bundles': [
      'Best value for money',
      'Complete wellness solution',
      'Multiple product benefits',
      'Perfect for getting started',
      'Save compared to individual items'
    ],
    'Enrollment Packs': [
      'Best value starter option',
      'Multiple product choices',
      'Perfect for new customers',
      'Significant savings included',
      'Complete wellness starter kit'
    ],
    'Equine': [
      'Designed for horses',
      'Same proven technology',
      'Safe and effective',
      'Easy to apply',
      'Supports equine wellness'
    ],
    'default': [
      'Premium quality product',
      'Scientifically formulated',
      'Easy to use',
      'Safe and natural',
      'Satisfaction guaranteed'
    ]
  },
  ro: {
    'Phototherapy Patches': [
      'Fără medicamente, chimicale sau stimulente',
      'Tehnologie de fototerapie brevetată',
      'Ușor de utilizat - aplici și gata',
      'Sistem de livrare non-transdermal',
      'Testat clinic și dovedit'
    ],
    'Y-Age System': [
      'Suport anti-îmbătrânire',
      'Promovează bunăstarea generală',
      'Fără medicamente sau chimicale',
      'Aplicare convenabilă a plasturelui',
      'Parte a unui sistem complet de wellness'
    ],
    'Alavida Skincare': [
      'Reduce aspectul ridurilor fine',
      'Luminează tenul',
      'Îmbunătățește fermitatea pielii',
      'Ingrediente naturale',
      'Formulă de grad profesional'
    ],
    'Bundles': [
      'Cel mai bun raport calitate-preț',
      'Soluție completă de wellness',
      'Beneficii multiple ale produselor',
      'Perfect pentru a începe',
      'Economisești comparativ cu articolele individuale'
    ],
    'Enrollment Packs': [
      'Cea mai bună opțiune de start',
      'Alegeri multiple de produse',
      'Perfect pentru clienții noi',
      'Economii semnificative incluse',
      'Kit complet de wellness pentru început'
    ],
    'Equine': [
      'Conceput pentru cai',
      'Aceeași tehnologie dovedită',
      'Sigur și eficient',
      'Ușor de aplicat',
      'Susține bunăstarea ecvină'
    ],
    'default': [
      'Produs de calitate premium',
      'Formulat științific',
      'Ușor de utilizat',
      'Sigur și natural',
      'Satisfacție garantată'
    ]
  },
  de: {
    'Phototherapy Patches': [
      'Keine Medikamente, Chemikalien oder Stimulanzien',
      'Patentierte Phototherapie-Technologie',
      'Einfach zu verwenden - einfach auftragen',
      'Nicht-transdermales Verabreichungssystem',
      'Klinisch getestet und bewährt'
    ],
    'Y-Age System': [
      'Anti-Aging-Unterstützung',
      'Fördert das allgemeine Wohlbefinden',
      'Keine Medikamente oder Chemikalien',
      'Bequeme Pflaster-Anwendung',
      'Teil eines kompletten Wellness-Systems'
    ],
    'Alavida Skincare': [
      'Reduziert das Erscheinungsbild feiner Linien',
      'Hellt den Teint auf',
      'Verbessert die Hautfestigkeit',
      'Natürliche Inhaltsstoffe',
      'Professionelle Formel'
    ],
    'Bundles': [
      'Bestes Preis-Leistungs-Verhältnis',
      'Komplette Wellness-Lösung',
      'Mehrere Produktvorteile',
      'Perfekt für den Einstieg',
      'Sparen im Vergleich zu Einzelartikeln'
    ],
    'Enrollment Packs': [
      'Beste Starter-Option',
      'Mehrere Produktauswahl',
      'Perfekt für neue Kunden',
      'Erhebliche Einsparungen enthalten',
      'Komplettes Wellness-Starterkit'
    ],
    'Equine': [
      'Entwickelt für Pferde',
      'Gleiche bewährte Technologie',
      'Sicher und wirksam',
      'Einfach anzuwenden',
      'Unterstützt das Wohlbefinden von Pferden'
    ],
    'default': [
      'Premium-Qualitätsprodukt',
      'Wissenschaftlich formuliert',
      'Einfach zu verwenden',
      'Sicher und natürlich',
      'Zufriedenheit garantiert'
    ]
  }
};

// Feature labels with translations
const featureLabels = {
  en: {
    phototherapy: 'Phototherapy',
    safeNatural: 'Safe & Natural',
    clinicallyTested: 'Clinically Tested'
  },
  ro: {
    phototherapy: 'Fototerapie',
    safeNatural: 'Sigur și Natural',
    clinicallyTested: 'Testat Clinic'
  },
  de: {
    phototherapy: 'Phototherapie',
    safeNatural: 'Sicher & Natürlich',
    clinicallyTested: 'Klinisch Getestet'
  }
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const { t, language, translateContent } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translatedDescription, setTranslatedDescription] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  // Get description in current language - with AI translation fallback
  useEffect(() => {
    const translateDescription = async () => {
      if (!product) return;
      
      // Get Romanian description (primary source)
      const roDesc = product.descriptions?.ro;
      // Get English description (fallback source)  
      const enDesc = product.descriptions?.en || product.description;
      
      // ROMANIAN: Use Romanian description if available
      if (language === 'ro') {
        if (roDesc) {
          setTranslatedDescription(roDesc);
        } else if (enDesc) {
          // Translate from English to Romanian
          setIsTranslating(true);
          try {
            const translated = await translateContent(enDesc, 'product', `${product.id}_ro`);
            setTranslatedDescription(translated);
          } catch (error) {
            setTranslatedDescription(enDesc);
          } finally {
            setIsTranslating(false);
          }
        }
        return;
      }
      
      // ENGLISH: Use English description if available
      if (language === 'en') {
        if (enDesc) {
          setTranslatedDescription(enDesc);
        } else if (roDesc) {
          // Translate from Romanian to English
          setIsTranslating(true);
          try {
            const translated = await translateContent(roDesc, 'product', `${product.id}_en`);
            setTranslatedDescription(translated);
          } catch (error) {
            setTranslatedDescription(roDesc);
          } finally {
            setIsTranslating(false);
          }
        }
        return;
      }
      
      // GERMAN (or other): Check for direct translation, else translate
      const directTranslation = product.descriptions?.[language];
      if (directTranslation) {
        setTranslatedDescription(directTranslation);
        return;
      }
      
      // Translate from Romanian (preferred) or English
      const sourceText = roDesc || enDesc;
      if (sourceText) {
        setIsTranslating(true);
        try {
          const translated = await translateContent(sourceText, 'product', `${product.id}_${language}`);
          setTranslatedDescription(translated);
        } catch (error) {
          setTranslatedDescription(sourceText);
        } finally {
          setIsTranslating(false);
        }
      }
    };
    
    translateDescription();
  }, [product, language, translateContent]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleBuyOnLifeWave = () => {
    // Use product-specific affiliate link if available, otherwise use default store
    const buyUrl = product?.affiliate_link || LIFEWAVE_SHOP_URL;
    
    // Open link FIRST (must be synchronous with user click to avoid popup blocker)
    window.open(buyUrl, '_blank');
    
    // Track the click (non-blocking)
    axios.post(`${API_URL}/api/track/affiliate-click`, {
      product_id: product?.id,
      product_name: product?.name,
      affiliate_link: buyUrl
    }).catch(error => console.error('Error tracking click:', error));
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Hi! I'm interested in ${product?.name}. Can you tell me more?`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-32 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-slate-200 rounded-2xl"></div>
              <div className="space-y-4">
                <div className="h-4 bg-slate-200 rounded w-24"></div>
                <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Product not found</h1>
          <Link to="/products">
            <Button>{t('backToProducts')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const langBenefits = productBenefits[language] || productBenefits['en'];
  const benefits = langBenefits[product.category] || langBenefits['default'];
  const features = featureLabels[language] || featureLabels['en'];
  const qvPoints = product.qv_points || product.pv_points || 0;

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('backToProducts')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full h-auto rounded-xl"
              />
            </div>
            
            {/* Price Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p>{t('pricingDisclaimer')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-sky-600 font-semibold uppercase tracking-wide text-sm mb-2">
                {product.category}
              </p>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                {product.name}
              </h1>
              <div className="text-slate-600 text-lg leading-relaxed">
                {isTranslating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-slate-400">Translating...</span>
                  </div>
                ) : (
                  <p>{translatedDescription}</p>
                )}
              </div>
            </div>

            {/* Price & Points */}
            <div className="flex items-center gap-6 py-6 border-y border-slate-200">
              <div>
                <span className="text-4xl font-bold text-slate-900">
                  ${product.price.toFixed(2)}
                </span>
                <p className="text-sm text-slate-500 mt-1">{t('priceExcl')}</p>
              </div>
              <div className="flex gap-4">
                <div className="bg-amber-100 text-amber-700 px-4 py-2 rounded-lg text-center">
                  <span className="text-2xl font-bold">{qvPoints}</span>
                  <p className="text-xs">QV Points</p>
                </div>
                <div className="bg-sky-100 text-sky-700 px-4 py-2 rounded-lg text-center">
                  <span className="text-2xl font-bold">{product.bv_points || 0}</span>
                  <p className="text-xs">BV Points</p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-sky-600" />
                {t('benefits')}
              </h3>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <Zap className="w-6 h-6 text-sky-600 mx-auto mb-2" />
                <p className="text-xs text-slate-600">{features.phototherapy}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-xs text-slate-600">{features.safeNatural}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <Award className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                <p className="text-xs text-slate-600">{features.clinicallyTested}</p>
              </div>
            </div>

            {/* Buy Button */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white rounded-full py-6 text-lg shadow-lg"
                onClick={handleBuyOnLifeWave}
                data-testid="buy-lifewave-btn"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                {t('buyOnLifeWave')}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                {t('purchaseDirectly')}
              </p>
            </div>

            {/* WhatsApp Contact */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-green-800">{t('contactMe')}</h4>
                  <p className="text-sm text-green-700">{t('questionsWhatsApp')}</p>
                </div>
                <Button
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-100 rounded-full"
                  onClick={handleWhatsApp}
                  data-testid="whatsapp-product-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <span className="text-sm text-slate-500">Share this product:</span>
              <SocialShareButtons 
                url={window.location.href} 
                title={`Check out ${product.name} from LifeWave!`} 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
